import { useState, useEffect, useRef } from 'react';
import {
  Modal,
  HeaderGlobalAction,
  TextInput,
  TextArea,
  Button,
  Select,
  SelectItem,
} from '@carbon/react';
import { Send, Erase } from '@carbon/icons-react';
import { useFetcher } from 'react-router';
import { useNotify } from './notify.tsx';
import { Icon } from './ui/icon.tsx';
import { cn } from '#app/utils/misc.tsx';
import * as Popover from '@radix-ui/react-popover';
import { EmojiPicker } from 'frimousse';

// Discord channels configuration
const CHANNELS = [
  {
    id: 'general',
    name: '#general',
    desc: 'Company-wide (Everyone)',
    env: 'DISCORD_GENERAL',
  },
  {
    id: 'admin',
    name: '#bahandi-admin',
    desc: 'Admin and HR only',
    env: 'DISCORD_BAHANDI_ADMIN',
  },
  {
    id: 'bahandi-app',
    name: '#bahandi-app',
    desc: 'Bahandi Updates',
    env: 'DISCORD_BAHANDI_APP',
  },
  {
    id: 'remote-time-logs',
    name: '#remote-time-logs',
    desc: 'User Time Logs',
    env: 'DISCORD_REMOTE_TIME_LOGS',
  },
];

export function BroadcastDispatcherModal() {
  const [isOpen, setIsOpen] = useState(false);
  const fetcher = useFetcher();
  const notify = useNotify();
  const lastProcessedRef = useRef<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Form state
  const [senderName, setSenderName] = useState('Bahandi');
  const [targetChannel, setTargetChannel] = useState('general');
  const [messageType, setMessageType] = useState('info');
  const [messageBody, setMessageBody] = useState('');

  // Custom Mode State
  const [customLabel, setCustomLabel] = useState('Special Event');
  const [customEmoji, setCustomEmoji] = useState('⭐');
  const [customColor, setCustomColor] = useState('#8b5cf6'); // Default purple

  // Markdown toolbar state
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

  // Custom preset emoji picker state
  const [customEmojiPickerOpen, setCustomEmojiPickerOpen] = useState(false);

  // User mention state
  const [userMentionInput, setUserMentionInput] = useState('');
  const [showUserMentionDialog, setShowUserMentionDialog] = useState(false);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleInsertUserMention = () => {
    if (!userMentionInput.trim()) {
      notify.error('Please enter a Discord User ID');
      return;
    }
    insertMarkdown(`<@${userMentionInput.trim()}>`, '', '');
    setUserMentionInput('');
    setShowUserMentionDialog(false);
  };

  // Insert markdown at cursor position
  const insertMarkdown = (
    prefix: string,
    suffix: string,
    placeholder: string,
  ) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = messageBody.substring(start, end);
    const textToInsert = selectedText || placeholder;

    const newText =
      messageBody.substring(0, start) +
      prefix +
      textToInsert +
      suffix +
      messageBody.substring(end);

    setMessageBody(newText);

    // Set cursor position after inserted text
    setTimeout(() => {
      const newCursorPos = start + prefix.length + textToInsert.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  const handleBroadcast = () => {
    if (!messageBody.trim()) {
      notify.error('Message body is required');
      return;
    }

    const formData = new FormData();
    formData.append('intent', 'broadcast');
    formData.append('senderName', senderName.trim());
    formData.append('targetChannel', targetChannel);
    formData.append('messageType', messageType);
    formData.append('messageBody', messageBody.trim());

    if (messageType === 'custom') {
      formData.append('customLabel', customLabel.trim());
      formData.append('customEmoji', customEmoji);
      formData.append('customColor', customColor);
    }

    fetcher.submit(formData, {
      method: 'POST',
      action: '/api/broadcast-discord',
    });
  };

  // Handle broadcast response
  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      const submissionId = JSON.stringify(fetcher.data);

      if (lastProcessedRef.current === submissionId) {
        return;
      }

      lastProcessedRef.current = submissionId;

      if (fetcher.data.status === 'success') {
        notify.success(fetcher.data.message);
        setMessageBody('');
        setIsOpen(false);
      } else if (fetcher.data.status === 'error') {
        notify.error(fetcher.data.message);
      }
    }
  }, [fetcher.state, fetcher.data, notify]);

  // Helper to determine the dynamic left border color of the Discord preview
  const getEmbedBorderClass = () => {
    if (messageType === 'info') return 'border-cyan-500';
    if (messageType === 'success') return 'border-green-500';
    if (messageType === 'update') return 'border-indigo-500';
    if (messageType === 'alert') return 'border-primary-base';
    return ''; // Custom handles via inline style
  };

  const isSending = fetcher.state === 'submitting';

  return (
    <>
      <HeaderGlobalAction
        aria-label="Broadcaster"
        tooltipAlignment="end"
        onClick={handleOpen}
      >
        <Icon
          name="misc/megaphone"
          className="w-5.5 h-5.5 text-white/70! hover:text-white!"
        />
      </HeaderGlobalAction>

      <Modal
        open={isOpen}
        onRequestClose={() => setIsOpen(false)}
        passiveModal
        modalHeading="Broadcaster"
        size="lg"
        className="[&_.cds--modal-content]:px-0! [&_.cds--modal-content]:pb-0! [&_.cds--modal-content]:mask-none! [&_.cds--modal-content]:max-h-[calc(100vh-8rem)]! [&_.cds--modal-content]:overflow-y-auto! [&_.cds--modal-container]:max-h-[calc(100vh-4rem)]!"
        selectorsFloatingMenus={[
          '.cds--overflow-menu-options',
          '.cds--tooltip',
          '.cds--list-box__menu',
          '.cds--list-box',
          '.cds--dropdown',
          '.cds--multiselect',
          '.emoji-picker-bypass',
        ]}
        preventCloseOnClickOutside
      >
        <div className="px-6 pb-6">
          {/* Warning Banner */}
          <div className="bg-amber-500/10 border border-amber-500/30 p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon
                name="radix/exclamation-triangle"
                className="w-5 h-5 text-amber-500 shrink-0 mt-0.5"
              />
              <div>
                <h4 className="text-xs font-bold text-amber-500 uppercase tracking-wider mb-1">
                  Live Production Warning
                </h4>
                <p className="text-[10px] text-amber-500/80 leading-relaxed font-medium">
                  Clicking broadcast fires a webhook immediately to the selected
                  Discord server. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Left Column - Configuration Form */}
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <TextInput
                    id="sender-name"
                    labelText="Display Name"
                    placeholder="e.g., Bahandi"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    required
                  />

                  <Select
                    id="target-channel"
                    labelText="Target Channel"
                    value={targetChannel}
                    onChange={(e) => setTargetChannel(e.target.value)}
                    required
                  >
                    {CHANNELS.map((c) => (
                      <SelectItem
                        key={c.id}
                        value={c.id}
                        text={`${c.name} — ${c.desc}`}
                      />
                    ))}
                  </Select>
                </div>

                {/* Message Type Selection */}
                <div className="space-y-3">
                  <label className="cds--label text-xs">
                    Announcement Type (Discord Style)
                  </label>

                  <div className="grid grid-cols-2 gap-3">
                    <label
                      className={`border p-3 cursor-pointer transition-colors flex items-center gap-3 ${messageType === 'info' ? 'border-cyan-500 bg-cyan-500/10' : 'border-border bg-background hover:bg-muted/5'}`}
                    >
                      <input
                        type="radio"
                        name="type"
                        value="info"
                        checked={messageType === 'info'}
                        onChange={(e) => setMessageType(e.target.value)}
                        className="sr-only"
                      />
                      <div
                        className={`w-3 h-3 rounded-none ${messageType === 'info' ? 'bg-cyan-500' : 'border border-border'}`}
                      ></div>
                      <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                        Standard
                      </span>
                    </label>

                    <label
                      className={`border p-3 cursor-pointer transition-colors flex items-center gap-3 ${messageType === 'success' ? 'border-green-500 bg-green-500/10' : 'border-border bg-background hover:bg-muted/5'}`}
                    >
                      <input
                        type="radio"
                        name="type"
                        value="success"
                        checked={messageType === 'success'}
                        onChange={(e) => setMessageType(e.target.value)}
                        className="sr-only"
                      />
                      <div
                        className={`w-3 h-3 rounded-none ${messageType === 'success' ? 'bg-green-500' : 'border border-border'}`}
                      ></div>
                      <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                        Celebration
                      </span>
                    </label>

                    <label
                      className={`border p-3 cursor-pointer transition-colors flex items-center gap-3 ${messageType === 'update' ? 'border-indigo-500 bg-indigo-500/10' : 'border-border bg-background hover:bg-muted/5'}`}
                    >
                      <input
                        type="radio"
                        name="type"
                        value="update"
                        checked={messageType === 'update'}
                        onChange={(e) => setMessageType(e.target.value)}
                        className="sr-only"
                      />
                      <div
                        className={`w-3 h-3 rounded-none ${messageType === 'update' ? 'bg-indigo-500' : 'border border-border'}`}
                      ></div>
                      <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                        App Update
                      </span>
                    </label>

                    <label
                      className={`border p-3 cursor-pointer transition-colors flex items-center gap-3 ${messageType === 'alert' ? 'border-primary-base bg-primary-base/10' : 'border-border bg-background hover:bg-muted/5'}`}
                    >
                      <input
                        type="radio"
                        name="type"
                        value="alert"
                        checked={messageType === 'alert'}
                        onChange={(e) => setMessageType(e.target.value)}
                        className="sr-only"
                      />
                      <div
                        className={`w-3 h-3 rounded-none ${messageType === 'alert' ? 'bg-primary-base' : 'border border-border'}`}
                      ></div>
                      <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                        Urgent Alert
                      </span>
                    </label>

                    {/* Custom Option spans full width below the 2x2 grid */}
                    <label
                      className={`col-span-2 border p-3 cursor-pointer transition-colors flex items-center gap-3 ${messageType === 'custom' ? 'border-foreground bg-muted/5' : 'border-border bg-background hover:bg-muted/5'}`}
                    >
                      <input
                        type="radio"
                        name="type"
                        value="custom"
                        checked={messageType === 'custom'}
                        onChange={(e) => setMessageType(e.target.value)}
                        className="sr-only"
                      />
                      <div
                        className={`w-3 h-3 rounded-none ${messageType === 'custom' ? 'bg-foreground' : 'border border-border'}`}
                      ></div>
                      <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                        Custom Preset
                      </span>
                    </label>
                  </div>

                  {/* Conditional Custom Configuration Panel */}
                  {messageType === 'custom' && (
                    <div className="p-5 border border-border bg-muted/5 mt-3 animate-in fade-in slide-in-from-top-2 space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                        <TextInput
                          id="custom-label"
                          labelText="Custom Title/Label"
                          placeholder="e.g., Special Event"
                          value={customLabel}
                          onChange={(e) => setCustomLabel(e.target.value)}
                        />

                        <div className="space-y-2">
                          <label className="cds--label text-xs">
                            Embed Color Hex
                          </label>
                          <div className="flex h-10">
                            <input
                              type="color"
                              value={customColor}
                              onChange={(e) => setCustomColor(e.target.value)}
                              className="w-10 h-full p-0 border-none bg-background cursor-pointer rounded-none"
                            />
                            <input
                              type="text"
                              value={customColor}
                              onChange={(e) => setCustomColor(e.target.value)}
                              className="flex-1 bg-background border border-border border-l-0 text-foreground text-sm px-3 focus:outline-none uppercase font-mono rounded-none cds--text-input"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="cds--label text-xs">
                          Select Emoji
                        </label>
                        <Popover.Root
                          open={customEmojiPickerOpen}
                          onOpenChange={setCustomEmojiPickerOpen}
                        >
                          <Popover.Trigger asChild>
                            <button
                              type="button"
                              className="flex items-center gap-3 p-3 border border-border bg-background hover:bg-muted/5 transition-colors w-full"
                            >
                              <span className="text-3xl">{customEmoji}</span>
                              <span className="text-xs text-muted-foreground">
                                Click to change emoji
                              </span>
                            </button>
                          </Popover.Trigger>
                          {customEmojiPickerOpen && (
                            <Popover.Portal
                              container={
                                typeof document !== 'undefined'
                                  ? document.body
                                  : undefined
                              }
                            >
                              <Popover.Content
                                className="emoji-picker-bypass bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 shadow-lg rounded-lg p-2 z-9999"
                                sideOffset={5}
                                align="start"
                                onOpenAutoFocus={(e) => e.preventDefault()}
                                onCloseAutoFocus={(e) => e.preventDefault()}
                                onInteractOutside={(e) => {
                                  e.preventDefault();
                                }}
                              >
                                <EmojiPicker.Root
                                  columns={8}
                                  onEmojiSelect={(emoji) => {
                                    setCustomEmoji(emoji.emoji);
                                    setCustomEmojiPickerOpen(false);
                                  }}
                                >
                                  <EmojiPicker.Search
                                    className="w-full p-2 mb-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                    placeholder="Search emoji..."
                                  />
                                  <EmojiPicker.Viewport className="max-h-80 overflow-y-auto">
                                    <EmojiPicker.Loading className="p-4 text-center text-gray-500">
                                      Loading…
                                    </EmojiPicker.Loading>
                                    <EmojiPicker.Empty className="p-4 text-center text-gray-500">
                                      No emoji found.
                                    </EmojiPicker.Empty>
                                    <EmojiPicker.List
                                      className="p-2"
                                      components={{
                                        CategoryHeader: ({
                                          category,
                                          ...props
                                        }) => (
                                          <div
                                            className="bg-white dark:bg-gray-800 pt-3 pb-1.5 font-medium text-gray-600 dark:text-gray-400 text-xs sticky top-0 z-10"
                                            {...props}
                                          >
                                            {category.label}
                                          </div>
                                        ),
                                        Row: ({ children, ...props }) => (
                                          <div
                                            className="flex gap-1 mb-1"
                                            {...props}
                                          >
                                            {children}
                                          </div>
                                        ),
                                        Emoji: ({ emoji, ...props }) => (
                                          <button
                                            className="flex size-8 items-center justify-center rounded-md text-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                            {...props}
                                          >
                                            {emoji.emoji}
                                          </button>
                                        ),
                                      }}
                                    />
                                  </EmojiPicker.Viewport>
                                </EmojiPicker.Root>
                              </Popover.Content>
                            </Popover.Portal>
                          )}
                        </Popover.Root>
                      </div>
                    </div>
                  )}
                </div>

                {/* Message Body */}
                <div className="space-y-2">
                  <label className="cds--label text-xs">Message Body</label>

                  {/* Discord-compatible Markdown Toolbar */}
                  <div className="flex items-center gap-0.5 p-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 overflow-x-auto">
                    <button
                      type="button"
                      title="Bold"
                      onClick={() => insertMarkdown('**', '**', 'bold text')}
                      className="flex items-center justify-center min-h-8 w-8 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <Icon name="carbon/text-bold" size="md" />
                    </button>
                    <button
                      type="button"
                      title="Italic"
                      onClick={() => insertMarkdown('*', '*', 'italic text')}
                      className="flex items-center justify-center min-h-8 w-8 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <Icon name="carbon/text-italic" size="md" />
                    </button>
                    <button
                      type="button"
                      title="Strikethrough"
                      onClick={() =>
                        insertMarkdown('~~', '~~', 'strikethrough')
                      }
                      className="flex items-center justify-center min-h-8 w-8 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <span className="text-sm font-bold">S</span>
                    </button>
                    <button
                      type="button"
                      title="Underline"
                      onClick={() =>
                        insertMarkdown('__', '__', 'underlined text')
                      }
                      className="flex items-center justify-center min-h-8 w-8 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <span className="text-sm font-bold underline">U</span>
                    </button>
                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-0.5" />
                    <button
                      type="button"
                      title="Inline Code"
                      onClick={() => insertMarkdown('`', '`', 'code')}
                      className="flex items-center justify-center min-h-8 w-8 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <Icon name="carbon/code" size="md" />
                    </button>
                    <button
                      type="button"
                      title="Code Block"
                      onClick={() =>
                        insertMarkdown('```\n', '\n```', 'code block')
                      }
                      className="flex items-center justify-center min-h-8 w-8 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <Icon name="carbon/code-block" size="md" />
                    </button>
                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-0.5" />
                    <button
                      type="button"
                      title="Blockquote"
                      onClick={() => insertMarkdown('> ', '', 'quote')}
                      className="flex items-center justify-center min-h-8 w-8 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <Icon name="carbon/quotes" size="md" />
                    </button>
                    <button
                      type="button"
                      title="Bullet List"
                      onClick={() => insertMarkdown('- ', '', 'List item')}
                      className="flex items-center justify-center min-h-8 w-8 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <Icon name="carbon/list" size="md" />
                    </button>
                    <button
                      type="button"
                      title="Numbered List"
                      onClick={() => insertMarkdown('1. ', '', 'List item')}
                      className="flex items-center justify-center min-h-8 w-8 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <Icon name="carbon/list-numbered" size="md" />
                    </button>
                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-0.5" />
                    <Popover.Root
                      open={emojiPickerOpen}
                      onOpenChange={setEmojiPickerOpen}
                    >
                      <Popover.Trigger asChild>
                        <button
                          type="button"
                          title="Insert Emoji"
                          className="flex items-center justify-center min-h-8 w-8 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                          <span className="text-lg">😀</span>
                        </button>
                      </Popover.Trigger>
                      {emojiPickerOpen && (
                        <Popover.Portal
                          container={
                            typeof document !== 'undefined'
                              ? document.body
                              : undefined
                          }
                        >
                          <Popover.Content
                            className="emoji-picker-bypass bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 shadow-lg rounded-lg p-2 z-9999"
                            sideOffset={5}
                            align="start"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                            onCloseAutoFocus={(e) => e.preventDefault()}
                            onInteractOutside={(e) => {
                              e.preventDefault();
                            }}
                          >
                            <EmojiPicker.Root
                              columns={8}
                              onEmojiSelect={(emoji) => {
                                insertMarkdown(emoji.emoji, '', '');
                                setEmojiPickerOpen(false);
                              }}
                            >
                              <EmojiPicker.Search
                                className="w-full p-2 mb-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                placeholder="Search emoji..."
                              />
                              <EmojiPicker.Viewport className="max-h-80 overflow-y-auto">
                                <EmojiPicker.Loading className="p-4 text-center text-gray-500">
                                  Loading…
                                </EmojiPicker.Loading>
                                <EmojiPicker.Empty className="p-4 text-center text-gray-500">
                                  No emoji found.
                                </EmojiPicker.Empty>
                                <EmojiPicker.List
                                  className="p-2"
                                  components={{
                                    CategoryHeader: ({
                                      category,
                                      ...props
                                    }) => (
                                      <div
                                        className="bg-white dark:bg-gray-800 pt-3 pb-1.5 font-medium text-gray-600 dark:text-gray-400 text-xs sticky top-0 z-10"
                                        {...props}
                                      >
                                        {category.label}
                                      </div>
                                    ),
                                    Row: ({ children, ...props }) => (
                                      <div
                                        className="flex gap-1 mb-1"
                                        {...props}
                                      >
                                        {children}
                                      </div>
                                    ),
                                    Emoji: ({ emoji, ...props }) => (
                                      <button
                                        className="flex size-8 items-center justify-center rounded-md text-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                        {...props}
                                      >
                                        {emoji.emoji}
                                      </button>
                                    ),
                                  }}
                                />
                              </EmojiPicker.Viewport>
                            </EmojiPicker.Root>
                          </Popover.Content>
                        </Popover.Portal>
                      )}
                    </Popover.Root>
                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-0.5" />
                    <Popover.Root
                      open={showUserMentionDialog}
                      onOpenChange={setShowUserMentionDialog}
                    >
                      <Popover.Trigger asChild>
                        <button
                          type="button"
                          title="Mention User"
                          className="flex items-center justify-center min-h-8 w-8 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                          <Icon name="carbon/at" size="md" />
                        </button>
                      </Popover.Trigger>
                      {showUserMentionDialog && (
                        <Popover.Portal
                          container={
                            typeof document !== 'undefined'
                              ? document.body
                              : undefined
                          }
                        >
                          <Popover.Content
                            className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 shadow-lg rounded-lg p-4 z-9999 w-80"
                            sideOffset={5}
                            align="start"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                            onCloseAutoFocus={(e) => e.preventDefault()}
                            onInteractOutside={(e) => {
                              e.preventDefault();
                            }}
                          >
                            <div className="space-y-3">
                              <div>
                                <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">
                                  Mention User
                                </h4>
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  Enter the Discord User ID to mention a user in
                                  your message.
                                </p>
                              </div>
                              <div className="space-y-2">
                                <label className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                                  Discord User ID
                                </label>
                                <input
                                  type="text"
                                  value={userMentionInput}
                                  onChange={(e) =>
                                    setUserMentionInput(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleInsertUserMention();
                                    }
                                  }}
                                  placeholder="e.g., 1234567890"
                                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  autoFocus
                                />
                              </div>
                              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-2">
                                <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                                  <strong>How to find User ID:</strong> In
                                  Discord, enable Developer Mode (User Settings
                                  → Developer → turn on Developer Mode), then
                                  right-click a user and select "Copy User ID"
                                </p>
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button
                                  kind="secondary"
                                  size="sm"
                                  type="button"
                                  onClick={() => {
                                    setUserMentionInput('');
                                    setShowUserMentionDialog(false);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  kind="primary"
                                  size="sm"
                                  type="button"
                                  onClick={handleInsertUserMention}
                                >
                                  Insert
                                </Button>
                              </div>
                            </div>
                          </Popover.Content>
                        </Popover.Portal>
                      )}
                    </Popover.Root>
                    <div className="ml-auto text-[9px] text-muted-foreground font-mono whitespace-nowrap">
                      Discord Markdown
                    </div>
                  </div>

                  <textarea
                    ref={textareaRef}
                    id="message-body"
                    rows={8}
                    value={messageBody}
                    onChange={(e) => setMessageBody(e.target.value)}
                    placeholder="Type your announcement here..."
                    required
                    className="w-full bg-background border border-border text-foreground text-sm p-4! focus:outline-none focus:border-primary-base transition-colors rounded-none resize-none"
                  />
                </div>

                <div className="pt-4 border-t border-border flex justify-end gap-3">
                  <Button
                    kind="secondary"
                    type="button"
                    renderIcon={Erase}
                    onClick={() => setMessageBody('')}
                  >
                    Clear
                  </Button>
                  <Button
                    kind="primary"
                    type="button"
                    onClick={handleBroadcast}
                    disabled={isSending || !messageBody.trim()}
                    renderIcon={Send}
                    className={cn(
                      isSending || !messageBody.trim()
                        ? 'dark:text-white/30! text-white/60!'
                        : 'dark:text-white! text-white!',
                    )}
                  >
                    {isSending ? 'Dispatching...' : 'Broadcast to Discord'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Right Column - Live Discord Preview */}
            <div className="lg:col-span-5">
              <div className="sticky top-6">
                <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-4 flex items-center gap-2">
                  <Icon name="radix/eye-open" className="w-3 h-3" /> Live
                  Preview
                </h3>

                {/* Discord Mockup Container */}
                <div className="bg-[#313338] border border-border text-[#dbdee1] p-4 font-sans text-sm shadow-xl relative overflow-hidden">
                  <div className="text-xs text-[#949ba4] border-b border-[#3f4147] pb-3 mb-4 font-semibold tracking-wide">
                    SENDING TO:{' '}
                    <span className="text-white">
                      {CHANNELS.find((c) => c.id === targetChannel)?.name ||
                        '#channel'}
                    </span>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-base flex items-center justify-center text-white font-bold shrink-0">
                      {senderName ? senderName.charAt(0).toUpperCase() : 'A'}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="font-medium text-white hover:underline cursor-pointer">
                          {senderName || 'Admin'}
                        </span>
                        <span className="text-[10px] bg-[#5865F2] text-white px-1.5 py-px rounded-sm font-semibold uppercase tracking-wider flex items-center gap-1">
                          <svg
                            className="w-3 h-3"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path>
                          </svg>
                          BOT
                        </span>
                        <span className="text-xs text-[#949ba4]">
                          Today at 1:53 PM
                        </span>
                      </div>

                      {/* Simulated Rich Embed */}
                      <div
                        className={`mt-2 border-l-4 bg-[#2b2d31] p-4 rounded-none ${messageType !== 'custom' ? getEmbedBorderClass() : ''}`}
                        style={
                          messageType === 'custom'
                            ? { borderLeftColor: customColor }
                            : {}
                        }
                      >
                        <div className="flex items-center gap-2 mb-2 font-bold text-white">
                          {/* Dynamic Emoji and Title Rendering */}
                          {messageType === 'info' && '📢 Official Announcement'}
                          {messageType === 'success' && '🎉 Good News!'}
                          {messageType === 'update' &&
                            '🔄 Bahandi System Update'}
                          {messageType === 'alert' && '⚠️ Urgent Alert'}
                          {messageType === 'custom' &&
                            `${customEmoji} ${customLabel || 'Custom Announcement'}`}
                        </div>

                        <div className="whitespace-pre-wrap leading-relaxed text-[#dbdee1] font-normal wrap-break-word">
                          {messageBody || (
                            <span className="text-[#949ba4] italic">
                              Your message will appear here...
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 border border-border bg-muted/5 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                    Webhook Status
                  </span>
                  <span className="text-[10px] font-bold text-green-500 uppercase tracking-widest flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    Ready to Fire
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
